import i18n from '@/i18n';
import { fetchImageBlob } from '@/pages/ipe/workbench/utils/goodsZipDownload';
import { API_BASE_URL, downloadCenterAPI } from '@/utils/api';
import { resolveCanonicalManagedUrlForExport } from '@/utils/exportPublicImageUrl';
import { getIpeUserLocalOwnerId, userLocal } from '@/utils/ipeUserLocalState';
import { ownerScopedIndexedDbName } from '@/utils/ipeUserIndexedDb';
import {
  isDocumentVisible,
  subscribePageVisibility,
} from '@/utils/pageVisibility';

/** 业务逻辑名；实际 localStorage key 为 ipe:u:{userId}:funding-download-center-v1 */
export const DOWNLOAD_CENTER_STORAGE_KEY = 'funding-download-center-v1';
/** 旧版全局 / 自管隔离 key，仅清理或一次性迁入 userLocal */
const LEGACY_GLOBAL_KEY = 'funding-download-center-v1';
const LEGACY_ANON_KEY = 'funding-download-center-v1:anon';
const LEGACY_SCOPED_PREFIX = 'funding-download-center-v1:u:';

const MAX_ITEMS = 200;
const listeners = new Set();
const zipBlobCache = new Map();
const ZIP_BLOB_CACHE_MAX = 24;

/** 客户端 JSZip 硬闸门：≥ 张数或估体积强制走服务端打包（方案书 A3） */
export const CLIENT_ZIP_MAX_ITEMS = 200;
export const CLIENT_ZIP_MAX_ESTIMATED_BYTES = 200 * 1024 * 1024;
/** 无 Content-Length 时按均张估算体积 */
export const CLIENT_ZIP_DEFAULT_BYTES_PER_ITEM = 1.5 * 1024 * 1024;

let serverDownloadSyncTimer = null;
let serverDownloadSyncInFlight = false;
let serverDownloadSyncVisibilityUnsub = null;

/** 前台同步间隔（有活跃服务端打包任务时） */
export const SERVER_DOWNLOAD_SYNC_VISIBLE_MS = 2000;
/** 后台 Tab 降频（仍推进进度，但不按 1～2s 打满 API） */
export const SERVER_DOWNLOAD_SYNC_HIDDEN_MS = 10_000;

/** 客户端 ZIP/文件 Blob 持久化（按用户隔离），避免刷新后「文件已失效」 */
const BLOB_DB_BASE = 'funding_download_center_blobs';
const BLOB_STORE = 'blobs';
const BLOB_DB_VERSION = 1;
/** @type {IDBDatabase|null} */
let blobDbInstance = null;
/** @type {string|null} */
let blobDbOwner = null;
/** @type {string|null} */
let blobDbName = null;

/** 上次已通知的 owner，用于换号时清内存队列（存储归属交给 ipeUserLocalState） */
let lastBoundOwnerUserId = null;

/** 客户端下载队列：避免大量打包任务同时执行导致浏览器卡顿 */
const downloadJobQueue = [];
let downloadQueueRunning = 0;
const MAX_CONCURRENT_DOWNLOAD_JOBS = 5;
const MAX_QUEUED_DOWNLOAD_JOBS = 500;

/** 进行中的任务：支持终止 */
const activeTasks = new Map();

function legacyScopedKeyFor(userId) {
  const id = String(userId || '').trim();
  return id ? `${LEGACY_SCOPED_PREFIX}${id}` : '';
}

function closeDownloadCenterBlobDb() {
  if (blobDbInstance) {
    try {
      blobDbInstance.close();
    } catch {
      /* ignore */
    }
  }
  blobDbInstance = null;
  blobDbOwner = null;
  blobDbName = null;
}

/** 换号 / 退出时关闭 Blob IDB 连接（库按用户隔离，不删数据） */
export function resetDownloadCenterBlobStoreForOwnerChange() {
  closeDownloadCenterBlobDb();
}

/** 下载中心用户可见文案（按当前界面语言渲染） */
function dcMsg(key, opts) {
  return i18n.t(`workbench:downloadCenterMsg.${key}`, opts);
}

function openDownloadCenterBlobDb() {
  return new Promise((resolve, reject) => {
    const owner = getIpeUserLocalOwnerId();
    const name = ownerScopedIndexedDbName(BLOB_DB_BASE);
    if (!owner || !name || typeof indexedDB === 'undefined') {
      reject(new Error(dcMsg('noOwner')));
      return;
    }
    if (blobDbInstance && blobDbOwner === owner && blobDbName === name) {
      resolve(blobDbInstance);
      return;
    }
    closeDownloadCenterBlobDb();
    const request = indexedDB.open(name, BLOB_DB_VERSION);
    request.onerror = () => reject(request.error || new Error(dcMsg('openDbFailed')));
    request.onsuccess = () => {
      blobDbInstance = request.result;
      blobDbOwner = owner;
      blobDbName = name;
      resolve(blobDbInstance);
    };
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(BLOB_STORE)) {
        db.createObjectStore(BLOB_STORE, { keyPath: 'id' });
      }
    };
  });
}

async function persistDownloadCenterBlob(recordId, blob) {
  const key = String(recordId || '').trim();
  if (!key || !blob) return;
  try {
    const db = await openDownloadCenterBlobDb();
    await new Promise((resolve, reject) => {
      const tx = db.transaction([BLOB_STORE], 'readwrite');
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error || new Error(dcMsg('writeBlobFailed')));
      tx.objectStore(BLOB_STORE).put({
        id: key,
        blob,
        createdAt: Date.now(),
      });
    });
  } catch (error) {
    console.warn('[下载中心] 持久化文件失败:', error);
  }
}

async function loadDownloadCenterBlob(recordId) {
  const key = String(recordId || '').trim();
  if (!key) return null;
  try {
    const db = await openDownloadCenterBlobDb();
    return await new Promise((resolve, reject) => {
      const tx = db.transaction([BLOB_STORE], 'readonly');
      const req = tx.objectStore(BLOB_STORE).get(key);
      req.onsuccess = () => {
        const value = req.result?.blob;
        resolve(value instanceof Blob ? value : null);
      };
      req.onerror = () => reject(req.error || new Error(dcMsg('readBlobFailed')));
    });
  } catch (error) {
    console.warn('[下载中心] 读取持久化文件失败:', error);
    return null;
  }
}

async function deleteDownloadCenterBlob(recordId) {
  const key = String(recordId || '').trim();
  if (!key) return;
  zipBlobCache.delete(key);
  try {
    const db = await openDownloadCenterBlobDb();
    await new Promise((resolve, reject) => {
      const tx = db.transaction([BLOB_STORE], 'readwrite');
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error || new Error(dcMsg('deleteBlobFailed')));
      tx.objectStore(BLOB_STORE).delete(key);
    });
  } catch (error) {
    console.warn('[下载中心] 删除持久化文件失败:', error);
  }
}

async function deleteDownloadCenterBlobs(recordIds = []) {
  const keys = [...new Set((recordIds || []).map((id) => String(id || '').trim()).filter(Boolean))];
  if (keys.length === 0) return;
  keys.forEach((key) => zipBlobCache.delete(key));
  try {
    const db = await openDownloadCenterBlobDb();
    await new Promise((resolve, reject) => {
      const tx = db.transaction([BLOB_STORE], 'readwrite');
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error || new Error(dcMsg('batchDeleteBlobFailed')));
      const store = tx.objectStore(BLOB_STORE);
      keys.forEach((key) => store.delete(key));
    });
  } catch (error) {
    console.warn('[下载中心] 批量删除持久化文件失败:', error);
  }
}

function notifyDownloadCenterListeners(items) {
  // 始终派发新数组，避免订阅方因引用不变而跳过渲染
  const snapshot = Array.isArray(items) ? items.map((item) => ({ ...item })) : [];
  listeners.forEach((fn) => {
    try {
      fn(snapshot);
    } catch (e) {
      console.warn('[下载中心] 通知订阅失败:', e);
    }
  });
}

function abortAllActiveDownloadTasks() {
  for (const [, task] of activeTasks) {
    if (!task?.abortController) continue;
    try {
      task.abortController.abort();
    } catch (_) {
      /* ignore */
    }
  }
  activeTasks.clear();
}

function clearDownloadCenterMemoryState() {
  abortAllActiveDownloadTasks();
  downloadJobQueue.length = 0;
  zipBlobCache.clear();
  recentDownloadAt.clear();
}

function dropUnscopedLegacyDownloadCenterStorage() {
  userLocal.dropLegacyKeys(LEGACY_GLOBAL_KEY, LEGACY_ANON_KEY);
}

/**
 * 将旧自管 key `funding-download-center-v1:u:{id}` 迁入 userLocal（仅当新命名空间为空）。
 * 迁完即删旧 key，避免双轨并存。
 */
function adoptLegacyScopedDownloadCenter(userId) {
  const owner = String(userId || '').trim();
  if (!owner || getIpeUserLocalOwnerId() !== owner) return;
  const legacyKey = legacyScopedKeyFor(owner);
  if (!legacyKey) return;
  try {
    const raw = localStorage.getItem(legacyKey);
    if (!raw) return;
    const existing = userLocal.getJson(DOWNLOAD_CENTER_STORAGE_KEY, null);
    if (!Array.isArray(existing) || existing.length === 0) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0) {
        userLocal.setJson(DOWNLOAD_CENTER_STORAGE_KEY, parsed.slice(0, MAX_ITEMS));
      }
    }
    localStorage.removeItem(legacyKey);
  } catch (e) {
    console.warn('[下载中心] 迁入旧隔离缓存失败:', e);
  }
}

/**
 * 登录 / 换号后刷新下载中心（须先 bindIpeUserLocalOwner）。
 * 存储统一走 userLocal；此处只负责迁旧 key、清内存队列、通知订阅者。
 */
export function bindDownloadCenterOwner(userId) {
  const next = String(userId ?? '').trim() || null;
  dropUnscopedLegacyDownloadCenterStorage();
  if (next && getIpeUserLocalOwnerId() === next) {
    adoptLegacyScopedDownloadCenter(next);
  }
  if (lastBoundOwnerUserId === next) {
    notifyDownloadCenterListeners(loadItems());
    return;
  }
  clearDownloadCenterMemoryState();
  lastBoundOwnerUserId = next;
  notifyDownloadCenterListeners(loadItems());
}

/**
 * 退出登录 / 会话失效：终止任务、清空内存与队列。
 * 保留 userLocal 中按用户隔离的历史，便于同账号再次登录恢复。
 */
export function clearDownloadCenterForLogout() {
  clearDownloadCenterMemoryState();
  closeDownloadCenterBlobDb();
  dropUnscopedLegacyDownloadCenterStorage();
  stopServerDownloadSyncLoop();
  lastBoundOwnerUserId = null;
  notifyDownloadCenterListeners([]);
}

/**
 * 写入内存缓存，并异步持久化到 IndexedDB（刷新后仍可从下载中心再次下载）。
 */
async function rememberDownloadCenterZipBlob(recordId, blob) {
  const key = String(recordId || '').trim();
  if (!key || !blob) return;
  zipBlobCache.set(key, blob);
  if (zipBlobCache.size > ZIP_BLOB_CACHE_MAX) {
    const drop = zipBlobCache.keys().next().value;
    // 仅淘汰内存，保留 IndexedDB，避免「已完成却点不了下载」
    if (drop && drop !== key) zipBlobCache.delete(drop);
  }
  await persistDownloadCenterBlob(key, blob);
}

/** 短时间窗口内同一 URL+文件名不重复下载（防连点、StrictMode、重复回调） */
const recentDownloadAt = new Map();
const RECENT_DOWNLOAD_MS = 20000;

function loadItems() {
  if (!getIpeUserLocalOwnerId()) return [];
  const parsed = userLocal.getJson(DOWNLOAD_CENTER_STORAGE_KEY, []);
  return Array.isArray(parsed) ? parsed : [];
}

function saveItems(items) {
  if (!getIpeUserLocalOwnerId()) {
    notifyDownloadCenterListeners([]);
    return [];
  }
  const next = items.slice(0, MAX_ITEMS);
  if (!userLocal.setJson(DOWNLOAD_CENTER_STORAGE_KEY, next)) {
    console.warn('[下载中心] 写入本地缓存失败');
  }
  notifyDownloadCenterListeners(next);
  return next;
}

export function getDownloadCenterItems() {
  return loadItems();
}

export function subscribeDownloadCenter(listener) {
  if (typeof listener === 'function') {
    listener(loadItems());
    listeners.add(listener);
  }
  return () => listeners.delete(listener);
}

export function addDownloadCenterRecord(record) {
  const items = loadItems();
  items.unshift(record);
  return saveItems(items);
}

export function patchDownloadCenterRecord(id, patch) {
  const items = loadItems();
  const idx = items.findIndex((item) => item.id === id);
  if (idx < 0) return items;
  items[idx] = { ...items[idx], ...patch };
  return saveItems(items);
}

/** 用服务端任务 id 替换本地占位记录（避免双记录） */
export function replaceDownloadCenterRecord(oldId, nextRecord) {
  const from = String(oldId || '').trim();
  const nextId = String(nextRecord?.id || '').trim();
  if (!nextId) return loadItems();
  const items = loadItems();
  const idx = from ? items.findIndex((item) => item.id === from) : -1;
  if (idx >= 0) {
    items[idx] = { ...items[idx], ...nextRecord, id: nextId };
  } else {
    const existing = items.findIndex((item) => item.id === nextId);
    if (existing >= 0) {
      items[existing] = { ...items[existing], ...nextRecord, id: nextId };
    } else {
      items.unshift({ ...nextRecord, id: nextId });
    }
  }
  if (from && from !== nextId && activeTasks.has(from)) {
    activeTasks.set(nextId, activeTasks.get(from));
    activeTasks.delete(from);
  }
  return saveItems(items);
}

/**
 * 从图片/条目对象收集已知字节数（忽略无效值）。
 * @param {Array<object|null|undefined>} [items]
 * @returns {number[]}
 */
export function collectKnownImageBytes(items = []) {
  const out = [];
  for (const item of Array.isArray(items) ? items : []) {
    const candidates = [
      item?.size,
      item?.fileSize,
      item?.byteSize,
      item?.image?.size,
      item?.image?.fileSize,
      item?.image?.byteSize,
    ];
    for (const raw of candidates) {
      const n = Number(raw);
      if (Number.isFinite(n) && n > 0) {
        out.push(n);
        break;
      }
    }
  }
  return out;
}

/**
 * 估算客户端 ZIP 将装入的图片总字节（未知张数按均张保守估算）。
 * @param {{ itemCount?: number, knownBytes?: Array<number|null|undefined>, defaultBytesPerItem?: number }} [opts]
 */
export function estimateClientZipBytes({
  itemCount = 0,
  knownBytes = [],
  defaultBytesPerItem = CLIENT_ZIP_DEFAULT_BYTES_PER_ITEM,
} = {}) {
  const count = Math.max(0, Number(itemCount) || 0);
  const known = (Array.isArray(knownBytes) ? knownBytes : [])
    .map((n) => Number(n))
    .filter((n) => Number.isFinite(n) && n > 0);
  const knownSum = known.reduce((sum, n) => sum + n, 0);
  const unknownCount = Math.max(0, count - known.length);
  const perItem = Math.max(0, Number(defaultBytesPerItem) || 0);
  return knownSum + unknownCount * perItem;
}

/**
 * 是否必须走服务端 ZIP（禁止浏览器 JSZip）
 * - 张数 ≥ CLIENT_ZIP_MAX_ITEMS，或
 * - 体积 ≥ CLIENT_ZIP_MAX_ESTIMATED_BYTES（显式 estimatedBytes；否则用 knownBytes + 均张估算）
 * @param {{ itemCount?: number, estimatedBytes?: number, knownBytes?: Array<number|null|undefined> }} [opts]
 */
export function shouldUseServerZipDownload({
  itemCount = 0,
  estimatedBytes,
  knownBytes,
} = {}) {
  const count = Math.max(0, Number(itemCount) || 0);
  if (count >= CLIENT_ZIP_MAX_ITEMS) return true;
  const explicitBytes = Number(estimatedBytes);
  const bytes = Number.isFinite(explicitBytes)
    ? explicitBytes
    : estimateClientZipBytes({ itemCount: count, knownBytes });
  return bytes >= CLIENT_ZIP_MAX_ESTIMATED_BYTES;
}

/** 服务端任务可下载分卷序号（无 parts 时视为单卷；失败/重试中卷不纳入批量保存） */
export function resolveServerVolumeIndexes(item) {
  const parts = Array.isArray(item?.parts) ? item.parts : [];
  if (parts.length > 0) {
    const downloadable = parts.filter((p) => {
      const status = String(p?.status || '').trim().toLowerCase();
      return !status || status === 'succeeded' || status === 'completed';
    });
    return downloadable
      .map((p) => Number(p?.index))
      .filter((n) => Number.isFinite(n) && n > 0)
      .sort((a, b) => a - b);
  }
  const volumeCount = Number(item?.volumeCount);
  if (Number.isFinite(volumeCount) && volumeCount > 1) {
    return Array.from({ length: Math.floor(volumeCount) }, (_, i) => i + 1);
  }
  return [1];
}

/** 规范化分卷 status（旧数据无 status 且有可下载痕迹时视为成功） */
export function normalizeDownloadPartStatus(part) {
  const status = String(part?.status || '').trim().toLowerCase();
  if (!status) {
    return part?.storageKey ? 'succeeded' : 'failed';
  }
  if (status === 'completed' || status === 'ok') return 'succeeded';
  if (status === 'downloading' || status === 'building') return 'pending';
  return status;
}

/** 供 UI 展示的分卷列表（按 index 排序） */
export function listDownloadCenterParts(item) {
  const parts = Array.isArray(item?.parts) ? item.parts : [];
  if (parts.length === 0) return [];
  return [...parts]
    .map((p) => ({
      ...p,
      index: Number(p?.index),
      status: normalizeDownloadPartStatus(p),
    }))
    .filter((p) => Number.isFinite(p.index) && p.index > 0)
    .sort((a, b) => a.index - b.index);
}

export function partStatusLabel(status) {
  const s = String(status || '').trim().toLowerCase();
  if (s === 'succeeded' || s === 'completed' || !s) {
    return i18n.t('workbench:downloadCenterPage.partReady');
  }
  if (s === 'failed') return i18n.t('workbench:downloadCenterPage.partFailed');
  if (s === 'pending') return i18n.t('workbench:downloadCenterPage.partRetrying');
  return i18n.t('workbench:downloadCenterPage.partProcessing');
}

/** 多卷、部分成功或存在失败/重试中卷时展示分卷条 */
export function shouldShowDownloadVolumeList(item) {
  const parts = listDownloadCenterParts(item);
  if (parts.length > 1) return true;
  if (item?.status === 'partial_succeeded') return true;
  return parts.some((p) => p.status === 'failed' || p.status === 'pending');
}

/**
 * T-B03/T-B04：仅失败卷重入队。
 * @returns {{ ok: boolean, message?: string, retried?: boolean }}
 */
export async function retryDownloadCenterPart(item, partIndex) {
  const id = String(item?.id || '').trim();
  const part = Number(partIndex);
  if (!id || !Number.isFinite(part) || part <= 0) {
    return { ok: false, message: dcMsg('invalidVolume') };
  }
  if (!item?.serverSide) {
    return { ok: false, message: dcMsg('retryOnlyServerVolume') };
  }
  if (item.status === 'downloading' || item.status === 'pending') {
    const parts = listDownloadCenterParts(item);
    const target = parts.find((p) => p.index === part);
    if (target?.status === 'pending') {
      return { ok: false, message: dcMsg('volumeRetrying') };
    }
  }
  try {
    const res = await downloadCenterAPI.retryPart(id, part);
    patchDownloadCenterRecord(id, {
      status: mapServerDownloadStatus(res?.status) || 'downloading',
      progress: Number.isFinite(Number(res?.progress)) ? Number(res.progress) : (item.progress || 5),
      error: res?.error || null,
      completedAt: null,
      parts: Array.isArray(res?.parts) ? res.parts : item.parts,
      volumeCount: res?.volumeCount ?? item.volumeCount,
      serverSide: true,
    });
    ensureServerDownloadSyncLoop();
    return { ok: true, retried: true };
  } catch (error) {
    return { ok: false, message: String(error?.message || error || dcMsg('volumeRetryFailed')) };
  }
}

/**
 * 仅保存某一成功卷到本地。
 * @returns {{ ok: boolean, message?: string }}
 */
export async function redownloadCenterPart(item, partIndex) {
  const id = String(item?.id || '').trim();
  const part = Number(partIndex);
  const fileName = item?.fileName;
  if (!id || !fileName || !Number.isFinite(part) || part <= 0) {
    return { ok: false, message: dcMsg('invalidVolume') };
  }
  if (!item?.serverSide) {
    return { ok: false, message: dcMsg('partDownloadOnlyServer') };
  }
  if (item.status === 'downloading' || item.status === 'pending') {
    return { ok: false, message: dcMsg('taskInProgress') };
  }
  const partMeta = listDownloadCenterParts(item).find((p) => p.index === part);
  if (partMeta && partMeta.status !== 'succeeded') {
    return { ok: false, message: dcMsg('volumeNotReady') };
  }
  try {
    const access = await downloadCenterAPI.getFileUrl(id, { part });
    const mode = String(access?.mode || '').trim().toLowerCase();
    const directUrl = String(access?.url || '').trim();
    const partName = String(
      access?.fileName
      || partMeta?.fileName
      || fileName,
    );
    if (Array.isArray(access?.parts) && access.parts.length > 0) {
      patchDownloadCenterRecord(id, { parts: access.parts, volumeCount: access.parts.length });
    }
    let triggered = false;
    if ((mode === 'direct' || mode === 'ticket') && directUrl) {
      const href = resolveAbsoluteDownloadUrl(directUrl);
      triggered = triggerBrowserUrlDownload(href, partName, {
        dedupeScope: `dl-center-server-${mode}-${id}-p${part}`,
        skipDedupe: true,
      });
    } else {
      triggered = await downloadAuthenticatedProxyFile(
        downloadCenterAPI.downloadFileUrl(id, { part }),
        partName,
      );
    }
    if (!triggered) {
      return { ok: false, message: dcMsg('savePickerCancelled') };
    }
    return { ok: true };
  } catch (error) {
    return { ok: false, message: String(error?.message || error || dcMsg('downloadFailed')) };
  }
}

function mapServerDownloadStatus(status) {
  const s = String(status || '').trim().toLowerCase();
  if (s === 'completed') return 'completed';
  if (s === 'partial_succeeded') return 'partial_succeeded';
  if (s === 'failed') return 'failed';
  if (s === 'expired') return 'expired';
  if (s === 'cancelled') return 'cancelled';
  return 'downloading';
}

/**
 * 将服务端任务 DTO 合并为本地记录（保留本地已有字段作回退）。
 * @param {object} remote
 * @param {object|null} prev
 */
export function mapRemoteDownloadTaskToRecord(remote, prev = null) {
  const id = String(remote?.id || prev?.id || '').trim();
  if (!id) return null;
  const status = mapServerDownloadStatus(remote?.status);
  const progressRaw = Number(remote?.progress);
  const terminal = status === 'completed'
    || status === 'partial_succeeded'
    || status === 'failed'
    || status === 'expired'
    || status === 'cancelled';
  const createdAt = remote?.createdAt
    || prev?.createdAt
    || new Date().toISOString();
  const completedAt = remote?.completedAt
    || (terminal ? (prev?.completedAt || new Date().toISOString()) : null);
  return {
    ...(prev && String(prev.id) === id ? prev : {}),
    id,
    fileName: String(remote?.fileName || prev?.fileName || 'download.zip'),
    source: remote?.source || prev?.source || '导出',
    status,
    progress: Number.isFinite(progressRaw)
      ? Math.max(0, Math.min(100, Math.round(progressRaw)))
      : (prev?.progress ?? (terminal ? 100 : 0)),
    error: remote?.error != null ? remote.error : (prev?.error || null),
    itemCount: remote?.itemCount ?? prev?.itemCount ?? null,
    createdAt,
    completedAt,
    downloadedAt: remote?.downloadedAt || prev?.downloadedAt || null,
    url: (status === 'completed' || status === 'partial_succeeded')
      ? downloadCenterAPI.downloadFileUrl(id)
      : (prev?.url || downloadCenterAPI.downloadFileUrl(id)),
    parts: Array.isArray(remote?.parts) ? remote.parts : prev?.parts,
    volumeCount: remote?.volumeCount ?? prev?.volumeCount,
    serverSide: true,
  };
}

function sortDownloadCenterItemsByCreatedDesc(items) {
  return [...items].sort((a, b) => {
    const ta = Date.parse(a?.createdAt) || 0;
    const tb = Date.parse(b?.createdAt) || 0;
    return tb - ta;
  });
}

/**
 * 从服务端拉取当前用户任务列表并并入本地缓存。
 * 修复：压测脚本 / 他端 API 建单后，下载中心仅读 localStorage 看不到记录。
 */
export async function hydrateDownloadCenterFromServer({ pageSize = MAX_ITEMS } = {}) {
  if (!getIpeUserLocalOwnerId()) return loadItems();
  const size = Math.min(MAX_ITEMS, Math.max(1, Number(pageSize) || MAX_ITEMS));
  try {
    const res = await downloadCenterAPI.listTasks({ page: 1, pageSize: size });
    const remotes = Array.isArray(res?.items) ? res.items : [];
    const locals = loadItems();
    const byId = new Map();
    for (const local of locals) {
      const id = String(local?.id || '').trim();
      if (id) byId.set(id, local);
    }
    for (const remote of remotes) {
      const id = String(remote?.id || '').trim();
      if (!id) continue;
      const mapped = mapRemoteDownloadTaskToRecord(remote, byId.get(id) || null);
      if (mapped) byId.set(id, mapped);
    }
    return saveItems(sortDownloadCenterItemsByCreatedDesc([...byId.values()]));
  } catch (error) {
    console.warn('[下载中心] 拉取服务端任务列表失败:', error);
    return loadItems();
  }
}

/** 将服务端任务进度同步到本地下载中心记录（按本地活跃 id 逐条拉取，不限 list 前 100） */
export async function syncServerDownloadTasks() {
  if (serverDownloadSyncInFlight) return loadItems();
  const locals = loadItems().filter(
    (item) => item?.serverSide && (item.status === 'downloading' || item.status === 'pending'),
  );
  if (locals.length === 0) return loadItems();
  serverDownloadSyncInFlight = true;
  try {
    const results = await Promise.allSettled(
      locals.map((local) => downloadCenterAPI.getTask(local.id)),
    );
    for (let i = 0; i < locals.length; i += 1) {
      const settled = results[i];
      if (settled.status !== 'fulfilled' || !settled.value) continue;
      const remote = settled.value;
      const local = locals[i];
      const mapped = mapRemoteDownloadTaskToRecord(remote, local);
      if (!mapped) continue;
      patchDownloadCenterRecord(local.id, mapped);
    }
  } catch (error) {
    console.warn('[下载中心] 同步服务端任务失败:', error);
  } finally {
    serverDownloadSyncInFlight = false;
  }
  return loadItems();
}

function hasActiveServerDownloadTasks() {
  return loadItems().some(
    (item) => item?.serverSide && (item.status === 'downloading' || item.status === 'pending'),
  );
}

function clearServerDownloadSyncTimer() {
  if (serverDownloadSyncTimer != null) {
    window.clearTimeout(serverDownloadSyncTimer);
    serverDownloadSyncTimer = null;
  }
}

function stopServerDownloadSyncVisibilityWatch() {
  if (typeof serverDownloadSyncVisibilityUnsub === 'function') {
    serverDownloadSyncVisibilityUnsub();
    serverDownloadSyncVisibilityUnsub = null;
  }
}

/** @internal 测试 / 登出清理 */
export function stopServerDownloadSyncLoop() {
  clearServerDownloadSyncTimer();
  stopServerDownloadSyncVisibilityWatch();
}

function scheduleServerDownloadSyncTick() {
  clearServerDownloadSyncTimer();
  if (typeof window === 'undefined') return;
  if (!hasActiveServerDownloadTasks()) {
    stopServerDownloadSyncVisibilityWatch();
    return;
  }
  const delay = isDocumentVisible()
    ? SERVER_DOWNLOAD_SYNC_VISIBLE_MS
    : SERVER_DOWNLOAD_SYNC_HIDDEN_MS;
  serverDownloadSyncTimer = window.setTimeout(async () => {
    serverDownloadSyncTimer = null;
    if (!hasActiveServerDownloadTasks()) {
      stopServerDownloadSyncVisibilityWatch();
      return;
    }
    try {
      await syncServerDownloadTasks();
    } catch {
      /* sync 内部已记日志 */
    }
    scheduleServerDownloadSyncTick();
  }, delay);
}

/**
 * 有服务端打包任务时启动轻量轮询；无任务时自动停止。
 * C3/P1：前台 2s、后台 Tab 10s（可见性感知），角标/列表页勿再另开 1s API 轮询。
 */
export function ensureServerDownloadSyncLoop() {
  if (typeof window === 'undefined') return;
  if (!hasActiveServerDownloadTasks()) {
    stopServerDownloadSyncLoop();
    void syncServerDownloadTasks();
    return;
  }
  if (!serverDownloadSyncVisibilityUnsub) {
    serverDownloadSyncVisibilityUnsub = subscribePageVisibility((visible) => {
      if (!hasActiveServerDownloadTasks()) {
        stopServerDownloadSyncLoop();
        return;
      }
      if (visible) {
        void syncServerDownloadTasks().finally(() => {
          scheduleServerDownloadSyncTick();
        });
        return;
      }
      scheduleServerDownloadSyncTick();
    });
  }
  if (serverDownloadSyncTimer != null) return;
  void syncServerDownloadTasks().finally(() => {
    scheduleServerDownloadSyncTick();
  });
}

export function clearCompletedDownloadCenterItems() {
  const prev = loadItems();
  const removedIds = prev
    .filter((item) => item.status !== 'downloading')
    .map((item) => item.id);
  const items = prev.filter((item) => item.status === 'downloading');
  const next = saveItems(items);
  void deleteDownloadCenterBlobs(removedIds);
  return next;
}

function sanitizeFileName(name) {
  return String(name || 'image.jpg').replace(/[\\/:*?"<>|]+/g, '_').trim() || 'image.jpg';
}

export function triggerBrowserFileDownload(blob, fileName, { dedupeScope = '', skipDedupe = false } = {}) {
  const safeName = sanitizeFileName(fileName);
  const dedupeKey = buildBlobDownloadDedupeKey(safeName, dedupeScope);
  if (!skipDedupe && wasRecentDownloadKey(dedupeKey)) {
    return false;
  }
  const blobUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = safeName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  if (!skipDedupe) {
    markRecentDownloadKey(dedupeKey);
  }
  window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
  return true;
}

/** 触发浏览器对远程 URL 的原生下载（不经 JS 整包进内存） */
export function triggerBrowserUrlDownload(url, fileName, { dedupeScope = '', skipDedupe = false } = {}) {
  const href = String(url || '').trim();
  if (!href) return false;
  const safeName = sanitizeFileName(fileName);
  const dedupeKey = buildBlobDownloadDedupeKey(safeName, dedupeScope || `url::${href.slice(0, 120)}`);
  if (!skipDedupe && wasRecentDownloadKey(dedupeKey)) {
    return false;
  }
  const link = document.createElement('a');
  link.href = href;
  link.download = safeName;
  link.rel = 'noopener';
  link.target = '_blank';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  if (!skipDedupe) {
    markRecentDownloadKey(dedupeKey);
  }
  return true;
}

/** 代理路径：优先 ticket 原生下载；其次 File System Access 流式；禁止大文件 blob */
const PROXY_BLOB_FALLBACK_MAX_BYTES = 32 * 1024 * 1024;

function resolveAbsoluteDownloadUrl(url) {
  const raw = String(url || '').trim();
  if (!raw) return '';
  if (/^https?:\/\//i.test(raw)) return raw;
  if (/^data:/i.test(raw)) return raw;
  if (raw.startsWith('/api/')) {
    try {
      const api = new URL(API_BASE_URL, window.location?.origin || 'http://localhost');
      return `${api.origin}${raw}`;
    } catch {
      return raw;
    }
  }
  if (raw.startsWith('/')) {
    try {
      return `${new URL(API_BASE_URL, window.location?.origin || 'http://localhost').origin}${raw}`;
    } catch {
      return raw;
    }
  }
  const base = String(API_BASE_URL || '').replace(/\/$/, '');
  return `${base}/${raw.replace(/^\//, '')}`;
}

async function downloadAuthenticatedProxyFile(fileUrl, fileName) {
  const absolute = resolveAbsoluteDownloadUrl(fileUrl);
  // ticket / 同域带签 URL：走浏览器原生下载，不经 JS 内存
  if (/[?&](sig|exp)=/.test(absolute) || /\/download-center\/tasks\/[^/]+\/file\?/.test(absolute)) {
    if (triggerBrowserUrlDownload(absolute, fileName, { skipDedupe: true })) {
      return true;
    }
  }

  const token = localStorage.getItem('ipe_auth_token') || localStorage.getItem('funding_auth_token');
  const res = await fetch(absolute, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    throw new Error(dcMsg('downloadFailedWithStatus', { status: res.status }));
  }
  const finalUrl = String(res.url || '');
  if (finalUrl && !finalUrl.includes('/download-center/tasks/') && res.redirected) {
    if (triggerBrowserUrlDownload(finalUrl, fileName, { skipDedupe: true })) {
      return true;
    }
  }

  const contentLength = Number(res.headers.get('content-length'));
  const safeName = sanitizeFileName(fileName);

  if (typeof window.showSaveFilePicker === 'function' && res.body) {
    try {
      const handle = await window.showSaveFilePicker({
        suggestedName: safeName,
        types: [{
          description: 'ZIP archive',
          accept: { 'application/zip': ['.zip'] },
        }],
      });
      const writable = await handle.createWritable();
      await res.body.pipeTo(writable);
      return true;
    } catch (error) {
      if (error?.name === 'AbortError') {
        return false;
      }
    }
  }

  if (!Number.isFinite(contentLength) || contentLength <= 0 || contentLength > PROXY_BLOB_FALLBACK_MAX_BYTES) {
    throw new Error(dcMsg('largeBlobBlocked'));
  }

  const blob = await res.blob();
  return triggerBrowserFileDownload(blob, safeName, {
    dedupeScope: `dl-center-proxy-${safeName}`,
    skipDedupe: true,
  });
}

function normalizeDownloadUrlKey(url) {
  return String(url || '').trim();
}

function buildDownloadDedupeKey(url, fileName, meta = {}) {
  const stamp = String(meta?.stampName || '').trim();
  const nameKey = sanitizeFileName(fileName);
  if (stamp && nameKey) return `${stamp}::${nameKey}`;
  const urlKey = normalizeDownloadUrlKey(url);
  return urlKey || nameKey;
}

function buildBlobDownloadDedupeKey(fileName, scope = '') {
  const nameKey = sanitizeFileName(fileName);
  return scope ? `blob::${scope}::${nameKey}` : `blob::${nameKey}`;
}

function wasRecentDownloadKey(key) {
  if (!key) return false;
  const last = recentDownloadAt.get(key);
  return Boolean(last && Date.now() - last < RECENT_DOWNLOAD_MS);
}

function markRecentDownloadKey(key) {
  if (!key) return;
  recentDownloadAt.set(key, Date.now());
  if (recentDownloadAt.size > 500) {
    const now = Date.now();
    for (const [k, t] of recentDownloadAt) {
      if (now - t > RECENT_DOWNLOAD_MS) recentDownloadAt.delete(k);
    }
  }
}

function isRecentDuplicateDownload(url, fileName, meta = {}) {
  return wasRecentDownloadKey(buildDownloadDedupeKey(url, fileName, meta));
}

export function isRecentDuplicateBlobDownload(fileName, scope = '') {
  return wasRecentDownloadKey(buildBlobDownloadDedupeKey(fileName, scope));
}

export function registerDownloadTaskAbort(id, abortController) {
  const key = String(id || '').trim();
  if (!key) return;
  activeTasks.set(key, { abortController, cancelled: false });
}

export function isDownloadTaskCancelled(id) {
  const key = String(id || '').trim();
  const task = activeTasks.get(key);
  return task?.cancelled === true;
}

export function terminateDownloadCenterTask(id) {
  const key = String(id || '').trim();
  if (!key) return false;
  const task = activeTasks.get(key);
  if (task?.abortController) {
    try {
      task.abortController.abort();
    } catch (_) {
      /* ignore */
    }
  }
  activeTasks.set(key, { ...(task || {}), cancelled: true });
  patchDownloadCenterRecord(key, {
    status: 'cancelled',
    error: '用户已终止',
    completedAt: new Date().toISOString(),
  });
  activeTasks.delete(key);
  void deleteDownloadCenterBlob(key);
  const item = loadItems().find((row) => row.id === key);
  if (item?.serverSide) {
    downloadCenterAPI.cancelTask(key).catch((error) => {
      console.warn('[下载中心] 服务端终止同步失败:', error);
    });
  }
  return true;
}

function finishActiveTask(id) {
  activeTasks.delete(String(id || '').trim());
}

function releaseDownloadQueueSlot() {
  downloadQueueRunning -= 1;
  drainDownloadJobQueue();
}

function drainDownloadJobQueue() {
  while (downloadQueueRunning < MAX_CONCURRENT_DOWNLOAD_JOBS && downloadJobQueue.length > 0) {
    const job = downloadJobQueue.shift();
    downloadQueueRunning += 1;
    Promise.resolve()
      .then(() => job())
      .catch((error) => {
        console.warn('[下载中心] 队列任务失败:', error);
      })
      .finally(releaseDownloadQueueSlot);
  }
}

/**
 * 将下载打包任务加入客户端队列（最多排队 500 个，同时执行 5 个）
 */
export function enqueueDownloadCenterJob(job) {
  if (typeof job !== 'function') {
    throw new Error(dcMsg('invalidTask'));
  }
  if (downloadJobQueue.length >= MAX_QUEUED_DOWNLOAD_JOBS) {
    throw new Error(dcMsg('queueFull'));
  }
  return new Promise((resolve, reject) => {
    downloadJobQueue.push(async () => {
      try {
        const result = await job();
        resolve(result);
      } catch (error) {
        reject(error);
        throw error;
      }
    });
    drainDownloadJobQueue();
  });
}

export function updateDownloadCenterProgress(id, progress) {
  const raw = Number(progress);
  if (!Number.isFinite(raw)) return;
  patchDownloadCenterRecord(id, {
    progress: Math.max(0, Math.min(100, Math.round(raw))),
  });
}

/**
 * 拉取图片并通过浏览器保存到本地，完成后写入下载中心
 * @param {{ skipBrowserDownload?: boolean }} options - 为 true 时仅写入记录，不触发浏览器保存
 */
export async function queueBrowserImageDownload({
  url,
  fileName,
  source = '导出',
  meta = {},
  skipBrowserDownload = false,
}) {
  const safeName = sanitizeFileName(fileName);
  if (isRecentDuplicateDownload(url, safeName, meta)) {
    return null;
  }

  const id = `dl-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const abortController = new AbortController();
  registerDownloadTaskAbort(id, abortController);

  const record = {
    id,
    fileName: safeName,
    url: String(url || ''),
    source,
    status: 'downloading',
    createdAt: new Date().toISOString(),
    completedAt: null,
    downloadedAt: null,
    error: null,
    progress: 0,
    ...meta,
  };
  addDownloadCenterRecord(record);

  try {
    if (isDownloadTaskCancelled(id)) {
      return null;
    }
    updateDownloadCenterProgress(id, 30);
    const blob = await fetchImageBlob(url);
    if (isDownloadTaskCancelled(id)) {
      return null;
    }
    updateDownloadCenterProgress(id, 90);

    if (skipBrowserDownload) {
      await rememberDownloadCenterZipBlob(id, blob);
      patchDownloadCenterRecord(id, {
        status: 'completed',
        completedAt: new Date().toISOString(),
        progress: 100,
      });
      finishActiveTask(id);
      return id;
    }

    const urlKey = buildDownloadDedupeKey(url, safeName, meta);
    if (!triggerBrowserFileDownload(blob, safeName)) {
      await rememberDownloadCenterZipBlob(id, blob);
      patchDownloadCenterRecord(id, {
        status: 'completed',
        completedAt: new Date().toISOString(),
        progress: 100,
        error: null,
      });
      finishActiveTask(id);
      return id;
    }
    markRecentDownloadKey(urlKey);
    // 即便已直接触发浏览器下载，也持久化一份，便于下载中心再次下载
    await rememberDownloadCenterZipBlob(id, blob);
    patchDownloadCenterRecord(id, {
      status: 'completed',
      completedAt: new Date().toISOString(),
      downloadedAt: new Date().toISOString(),
      progress: 100,
    });
    finishActiveTask(id);
    return id;
  } catch (error) {
    if (isDownloadTaskCancelled(id)) {
      return null;
    }
    patchDownloadCenterRecord(id, {
      status: 'failed',
      error: String(error?.message || error),
      completedAt: new Date().toISOString(),
      progress: 100,
    });
    finishActiveTask(id);
    throw error;
  }
}

function dedupeDownloadJobs(jobs) {
  const seen = new Set();
  const list = [];
  for (const job of jobs || []) {
    const key = buildDownloadDedupeKey(job.url, job.fileName, job.meta);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    list.push(job);
  }
  return list;
}

/**
 * 按模版导出后批量触发图片下载（串行间隔，降低浏览器拦截概率）
 * 多张图片仅写入下载中心，不直接触发浏览器保存
 */
export async function runExportImageDownloadBatch(jobs, { delayMs = 400 } = {}) {
  const unique = dedupeDownloadJobs(jobs);
  for (let i = 0; i < unique.length; i += 1) {
    const job = unique[i];
    try {
      await enqueueDownloadCenterJob(() =>
        queueBrowserImageDownload({ ...job, skipBrowserDownload: true }),
      );
    } catch (error) {
      console.warn('[导出] 图片下载失败:', job.fileName, error);
    }
    if (delayMs > 0 && i < unique.length - 1) {
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
  }
  return unique.length;
}

/**
 * 将任意 Blob 写入下载中心（不触发浏览器保存，需在下载中心点击「下载」）
 * @returns {Promise<string|null>} 下载中心记录 id
 */
export async function queueBrowserBlobDownload({
  blob,
  fileName,
  source = '导出',
  meta = {},
  recordId = '',
}) {
  if (!blob) throw new Error(dcMsg('invalidContent'));

  const safeName = sanitizeFileName(fileName || 'download.bin');
  const id = String(recordId || '').trim()
    || `dl-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const existing = loadItems().find((item) => item.id === id);

  if (!existing) {
    addDownloadCenterRecord({
      id,
      fileName: safeName,
      url: '',
      source,
      status: 'downloading',
      createdAt: new Date().toISOString(),
      completedAt: null,
      downloadedAt: null,
      error: null,
      progress: 100,
      ...meta,
    });
  } else {
    patchDownloadCenterRecord(id, {
      fileName: safeName,
      source,
      status: 'downloading',
      error: null,
      completedAt: null,
      downloadedAt: null,
      progress: 100,
      ...meta,
    });
  }

  try {
    if (isDownloadTaskCancelled(id)) {
      return null;
    }
    await rememberDownloadCenterZipBlob(id, blob);
    patchDownloadCenterRecord(id, {
      status: 'completed',
      completedAt: new Date().toISOString(),
      progress: 100,
    });
    finishActiveTask(id);
    return id;
  } catch (error) {
    patchDownloadCenterRecord(id, {
      status: 'failed',
      error: String(error?.message || error),
      completedAt: new Date().toISOString(),
      progress: 100,
    });
    finishActiveTask(id);
    throw error;
  }
}

/**
 * 将 ZIP 写入下载中心（不触发浏览器保存，需在下载中心点击「下载」）
 * @returns {Promise<string|null>} 下载中心记录 id
 */
export async function queueBrowserZipDownload({
  blob,
  fileName,
  source = '导出',
  meta = {},
  recordId = '',
}) {
  const rawName = String(fileName || 'download.zip');
  const zipName = rawName.toLowerCase().endsWith('.zip') ? rawName : `${rawName}.zip`;
  return queueBrowserBlobDownload({
    blob,
    fileName: zipName,
    source,
    meta,
    recordId,
  });
}

export function countActiveDownloads(items = loadItems()) {
  return items.filter((item) => item.status === 'downloading' || item.status === 'pending').length;
}

export async function redownloadCenterItem(item) {
  const id = String(item?.id || '').trim();
  const fileName = item?.fileName;
  if (!id || !fileName) {
    return { ok: false, message: dcMsg('invalidRecord') };
  }
  if (item.status === 'downloading' || item.status === 'pending') {
    return { ok: false, message: dcMsg('taskInProgress') };
  }

  const now = new Date().toISOString();

  // 失败：重新入队整包打包（partial_succeeded 先下成功卷；整包/单卷重试见 T-B03/T-B04）
  if (item?.serverSide && item.status === 'failed') {
    try {
      const res = await downloadCenterAPI.retryTask(id);
      patchDownloadCenterRecord(id, {
        status: 'downloading',
        progress: Number(res?.progress) || 0,
        error: null,
        completedAt: null,
        downloadedAt: null,
        serverSide: true,
      });
      ensureServerDownloadSyncLoop();
      return { ok: true, retried: true };
    } catch (error) {
      return { ok: false, message: String(error?.message || error || dcMsg('repackFailed')) };
    }
  }

  if (item?.serverSide) {
    try {
      const volumeIndexes = resolveServerVolumeIndexes(item);
      let saved = 0;
      for (const partIndex of volumeIndexes) {
        const access = await downloadCenterAPI.getFileUrl(id, { part: partIndex });
        const mode = String(access?.mode || '').trim().toLowerCase();
        const directUrl = String(access?.url || '').trim();
        const partName = String(
          access?.fileName
          || item?.parts?.find((p) => Number(p?.index) === partIndex)?.fileName
          || fileName,
        );
        if (Array.isArray(access?.parts) && access.parts.length > 0) {
          patchDownloadCenterRecord(id, { parts: access.parts, volumeCount: access.parts.length });
        }

        let triggered = false;
        if ((mode === 'direct' || mode === 'ticket') && directUrl) {
          const href = resolveAbsoluteDownloadUrl(directUrl);
          triggered = triggerBrowserUrlDownload(href, partName, {
            dedupeScope: `dl-center-server-${mode}-${id}-p${partIndex}`,
            skipDedupe: true,
          });
        } else {
          triggered = await downloadAuthenticatedProxyFile(
            downloadCenterAPI.downloadFileUrl(id, { part: partIndex }),
            partName,
          );
        }
        if (!triggered) {
          return {
            ok: false,
            message: volumeIndexes.length > 1
              ? dcMsg('volumeNotSaved', { index: partIndex, saved })
              : dcMsg('savePickerCancelled'),
          };
        }
        saved += 1;
        if (volumeIndexes.length > 1 && partIndex < volumeIndexes[volumeIndexes.length - 1]) {
          await new Promise((r) => window.setTimeout(r, 400));
        }
      }

      patchDownloadCenterRecord(id, { downloadedAt: now });
      try {
        await downloadCenterAPI.markDownloaded(id);
      } catch (_) {
        /* ignore sync failure */
      }
      return { ok: true, volumeCount: saved };
    } catch (error) {
      return { ok: false, message: String(error?.message || error || dcMsg('downloadFailed')) };
    }
  }

  const cachedBlob = zipBlobCache.get(id) || await loadDownloadCenterBlob(id);
  if (cachedBlob) {
    if (!zipBlobCache.has(id)) {
      zipBlobCache.set(id, cachedBlob);
    }
    const triggered = triggerBrowserFileDownload(cachedBlob, fileName, {
      dedupeScope: `dl-center-${id}`,
      skipDedupe: true,
    });
    if (triggered) {
      patchDownloadCenterRecord(id, { downloadedAt: now });
      return { ok: true };
    }
    return { ok: false, message: dcMsg('savePickerNotOpened') };
  }

  const url = String(item.url || '').trim();
  if (!url) {
    return { ok: false, message: dcMsg('fileExpired') };
  }

  try {
    const blob = await fetchImageBlob(url);
    const triggered = triggerBrowserFileDownload(blob, fileName, {
      dedupeScope: `dl-center-${id}`,
      skipDedupe: true,
    });
    if (triggered) {
      patchDownloadCenterRecord(id, { downloadedAt: now });
      return { ok: true };
    }
    return { ok: false, message: dcMsg('savePickerNotOpened') };
  } catch (error) {
    return { ok: false, message: String(error?.message || error || dcMsg('downloadFailed')) };
  }
}

/**
 * 提交服务端下载任务（RabbitMQ 队列消费打包，适合大批量）
 * @param {{ fileName: string, source?: string, items: Array<{url:string,path:string}>, recordId?: string, required?: boolean }} opts
 * @returns {Promise<{ id: string, serverSide: true }|null>}
 */
export async function submitServerDownloadTask({
  fileName,
  source,
  items,
  recordId,
  required = false,
}) {
  if (!Array.isArray(items) || items.length === 0) return null;
  // 服务端优先短引用：受管图压成 /uploads/{key}，避免预签名长 URL 撑爆 DB；外链仍传绝对地址
  const normalizedItems = items
    .map((item) => {
      const path = String(item?.path || '').trim();
      const rawUrl = String(item?.url || '').trim();
      if (!path || !rawUrl) return null;
      const canonical = resolveCanonicalManagedUrlForExport(rawUrl);
      // 短引用保留相对 /uploads/...，由服务端 Blob 直读；外链再补绝对 URL
      const url = canonical || resolveAbsoluteDownloadUrl(rawUrl);
      if (!url) return null;
      return { path, url };
    })
    .filter(Boolean);
  if (normalizedItems.length === 0) {
    if (required) {
      throw new Error(dcMsg('noValidImageUrl'));
    }
    return null;
  }
  const localId = String(recordId || '').trim();
  try {
    const res = await downloadCenterAPI.createTask({
      fileName,
      source,
      items: normalizedItems,
      clientRecordId: localId || undefined,
    });
    const id = String(res?.id || '').trim();
    if (!id) {
      if (required) {
        throw new Error(dcMsg('noTaskId'));
      }
      return null;
    }
    const record = {
      id,
      fileName: String(fileName || 'download.zip'),
      url: downloadCenterAPI.downloadFileUrl(id),
      source: source || '导出',
      status: 'downloading',
      createdAt: new Date().toISOString(),
      completedAt: null,
      downloadedAt: null,
      error: null,
      itemCount: normalizedItems.length,
      progress: Number(res?.progress) || 0,
      serverSide: true,
    };
    if (localId) {
      replaceDownloadCenterRecord(localId, record);
    } else {
      addDownloadCenterRecord(record);
    }
    ensureServerDownloadSyncLoop();
    return { id, serverSide: true };
  } catch (error) {
    if (required) {
      const message = String(error?.message || error || dcMsg('serverUnavailable'));
      if (localId) {
        patchDownloadCenterRecord(localId, {
          status: 'failed',
          error: message,
          completedAt: new Date().toISOString(),
          progress: 100,
        });
      }
      throw error instanceof Error ? error : new Error(message);
    }
    console.warn('[下载中心] 服务端任务提交失败，回退客户端打包:', error);
    return null;
  }
}
